// =====================================================================
// BrandingContext — runtime app branding (in-app logo + browser favicon)
// fetched from GET /api/public/branding on boot and applied over the
// bundled Vite defaults.
//
// Why runtime, not build-time: the frontend ships as an immutable nginx
// image with no writable volume, so an uploaded logo/favicon cannot be a
// static-file swap — the app must read the override at runtime. The GET is
// public (favicon + login-page logo render before anyone authenticates),
// so this loads regardless of auth and is mounted ABOVE AuthProvider.
//
// The favicon is applied by mutating the document's <link rel="icon">
// href to the uploaded data: URL (a valid icon href in every modern
// browser) — index.html's static /iewrs-icon.png stays as the pre-fetch
// fallback and the "reset" target. The logo is exposed via useBranding()
// for BrandMark to resolve override-first against its bundled default.
// =====================================================================
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { brandingAPI } from '../services/api'
import { normalizeBranding } from './brandingLogic.mjs'

const Context = createContext({
  branding: { logo: null, favicon: null },
  loading: true,
  reload: () => {},
})

// Point every favicon/apple-touch link at `href`, or restore the static
// default index.html shipped when `href` is null. The first override
// stashes the original href in a data attribute so a later reset can put
// it back without hardcoding the path here.
function applyFavicon(href) {
  if (typeof document === 'undefined') return
  const links = document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]')
  links.forEach((link) => {
    if (link.dataset.brandingDefault === undefined) {
      link.dataset.brandingDefault = link.getAttribute('href') || ''
    }
    const target = href || link.dataset.brandingDefault
    if (target) link.setAttribute('href', target)
  })
}

export function BrandingProvider({ children }) {
  const [state, setState] = useState({ branding: { logo: null, favicon: null }, loading: true })

  const reload = async () => {
    try {
      // The response interceptor unwraps to the JSON body, so this is
      // { success, data: { logo, favicon }, timestamp }.
      const body = await brandingAPI.get()
      const branding = normalizeBranding(body?.data ?? body)
      setState({ branding, loading: false })
      applyFavicon(branding.favicon)
    } catch {
      // Best-effort: a failed fetch leaves every surface on its bundled
      // default rather than blanking the logo or throwing at the root.
      setState({ branding: { logo: null, favicon: null }, loading: false })
    }
  }

  useEffect(() => { reload() }, [])

  const value = useMemo(() => ({ ...state, reload }), [state])
  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const useBranding = () => useContext(Context)
